import { PortalLayout } from '@/components/contractor-portal/PortalLayout';
import { PortalRouteGuard } from '@/components/contractor-portal/PortalRouteGuard';
import { PortalDocumentUpload } from '@/components/contractor-portal/PortalDocumentUpload';
import { DocumentList } from '@/components/partners/DocumentList';
import { useContractorProfile } from '@/hooks/useContractorProfile';
import { usePartnerDocuments } from '@/hooks/usePartnerDocuments';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';

export default function ContractorPortalDocuments() {
  const { profile } = useContractorProfile();
  const { documents, isLoading, deleteDocument } = usePartnerDocuments(profile?.company_id || null);

  const verifiedDocs = documents.filter(d => d.verification_status === 'verified');
  const pendingDocs = documents.filter(d => d.verification_status === 'pending');
  const rejectedDocs = documents.filter(d => d.verification_status === 'rejected');

  const canDelete = (doc: any) => {
    // Contractors can only delete their own uploads
    return doc.uploaded_by_contractor_user_id === profile?.auth_user_id;
  };

  return (
    <PortalRouteGuard>
      <PortalLayout>
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Documents</h1>
              <p className="text-muted-foreground mt-1">
                Manage your prequalification documents
              </p>
            </div>
            <PortalDocumentUpload />
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Verified</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{verifiedDocs.length}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending</CardTitle>
                <Clock className="h-4 w-4 text-yellow-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{pendingDocs.length}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Rejected</CardTitle>
                <AlertTriangle className="h-4 w-4 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{rejectedDocs.length}</div>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="all" className="space-y-4">
            <TabsList>
              <TabsTrigger value="all">All Documents ({documents.length})</TabsTrigger>
              <TabsTrigger value="verified">Verified ({verifiedDocs.length})</TabsTrigger>
              <TabsTrigger value="pending">Pending ({pendingDocs.length})</TabsTrigger>
              <TabsTrigger value="rejected">Rejected ({rejectedDocs.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              <DocumentList 
                companyId={profile?.company_id || ''} 
                isContractorView={true}
                contractorUserId={profile?.id}
              />
            </TabsContent>

            <TabsContent value="verified">
              <DocumentList 
                companyId={profile?.company_id || ''} 
                isContractorView={true}
                contractorUserId={profile?.id}
              />
            </TabsContent>

            <TabsContent value="pending">
              <DocumentList 
                companyId={profile?.company_id || ''} 
                isContractorView={true}
                contractorUserId={profile?.id}
              />
            </TabsContent>

            <TabsContent value="rejected">
              {rejectedDocs.length > 0 && (
                <div className="mb-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-lg p-4">
                  <p className="text-sm text-red-800 dark:text-red-200">
                    The documents below were rejected. Please review the notes and upload corrected versions.
                  </p>
                </div>
              )}
              <DocumentList 
                companyId={profile?.company_id || ''} 
                isContractorView={true}
                contractorUserId={profile?.id}
              />
            </TabsContent>
          </Tabs>
        </div>
      </PortalLayout>
    </PortalRouteGuard>
  );
}
